import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import { api, jsonBody } from "../api";
import type { Client } from "../types";
import { PageHeader } from "../components/PageHeader";
import { StatusChip } from "../components/StatusChip";
import { StatCard } from "../components/StatCard";

interface Detail {
  client: Client;
  apiKeys: any[];
  usagePeriods: any[];
  adjustments: any[];
  recentScans: any[];
  webhooks: any[];
}
export function ClientDetailPage() {
  const { clientId = "" } = useParams();
  const [data, setData] = useState<Detail | null>(null);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState("");
  const [keyDialog, setKeyDialog] = useState(false);
  const [usageDialog, setUsageDialog] = useState(false);
  const [webhookDialog, setWebhookDialog] = useState(false);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(
    null,
  );
  const load = () =>
    api<Detail>(`/admin/clients/${clientId}`)
      .then(setData)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [clientId]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress />;
  const c = data.client;
  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={c.id}
        actions={
          <>
            <StatusChip value={c.status} />
            <TextField
              select
              size="small"
              value={c.status}
              onChange={async (e) => {
                await api(`/admin/clients/${c.id}/status`, {
                  method: "PATCH",
                  body: jsonBody({ status: e.target.value }),
                });
                void load();
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="DELETED">Deleted</MenuItem>
            </TextField>
          </>
        }
      />
      <div className="metric-grid">
        <StatCard
          label="Monthly usage"
          value={`${c.monthlyImageUsed} / ${c.monthlyImageLimit}`}
        />
        <StatCard label="Plan" value={c.plan} />
        <StatCard label="Default currency" value={c.defaultCurrency} />
        <StatCard label="Retention" value={`${c.retentionDays} days`} />
      </div>
      <Card sx={{ mt: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab label="Overview" />
          <Tab label={`API keys (${data.apiKeys.length})`} />
          <Tab label="Usage" />
          <Tab label="Recent scans" />
          <Tab label="Webhooks" />
        </Tabs>
        <Divider />
        {tab === 0 && <Overview client={c} onSaved={load} />}{" "}
        {tab === 1 && (
          <Box p={2.5}>
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setKeyDialog(true)}
              >
                Generate key
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prefix</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.apiKeys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="mono">{k.key_prefix}</TableCell>
                    <TableCell>
                      <StatusChip value={k.status} />
                    </TableCell>
                    <TableCell>{date(k.created_at)}</TableCell>
                    <TableCell>{date(k.last_used_at)}</TableCell>
                    <TableCell>{date(k.expires_at)}</TableCell>
                    <TableCell>
                      {k.status === "ACTIVE" && (
                        <Tooltip title="Revoke">
                          <IconButton
                            color="error"
                            onClick={async () => {
                              if (confirm("Revoke this API key?")) {
                                await api(
                                  `/admin/clients/${c.id}/api-keys/${k.id}/revoke`,
                                  { method: "POST" },
                                );
                                void load();
                              }
                            }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {tab === 2 && (
          <Box p={2.5}>
            <Stack direction="row" justifyContent="flex-end" gap={1} mb={2}>
              <Button
                variant="outlined"
                onClick={async () => {
                  const reason = prompt("Reason for resetting usage");
                  if (reason) {
                    await api(`/admin/clients/${c.id}/usage-reset`, {
                      method: "POST",
                      body: jsonBody({ reason }),
                    });
                    void load();
                  }
                }}
              >
                Reset usage
              </Button>
              <Button variant="contained" onClick={() => setUsageDialog(true)}>
                Adjust usage
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Reserved</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Failed</TableCell>
                  <TableCell>Adjusted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.usagePeriods.map((p) => (
                  <TableRow key={p.period_start}>
                    <TableCell>{p.period_start}</TableCell>
                    <TableCell>{p.reserved_units}</TableCell>
                    <TableCell>{p.completed_units}</TableCell>
                    <TableCell>{p.failed_units}</TableCell>
                    <TableCell>{p.adjusted_units}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {tab === 3 && (
          <Box p={2.5}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Scan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Images</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentScans.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="mono">{s.id}</TableCell>
                    <TableCell>
                      <StatusChip value={s.status} />
                    </TableCell>
                    <TableCell>{s.image_count}</TableCell>
                    <TableCell>{date(s.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {tab === 4 && (
          <Box p={2.5}>
            <Stack direction="row" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setWebhookDialog(true)}
              >
                Add webhook
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell>Events</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.webhooks.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>{w.url}</TableCell>
                    <TableCell>{w.events}</TableCell>
                    <TableCell>
                      <StatusChip value={w.status} />
                    </TableCell>
                    <TableCell>
                      {w.status === "ACTIVE" && (
                        <Button
                          color="error"
                          onClick={async () => {
                            await api(
                              `/admin/clients/${c.id}/webhooks/${w.id}/disable`,
                              { method: "POST" },
                            );
                            void load();
                          }}
                        >
                          Disable
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
      <GenerateKeyDialog
        open={keyDialog}
        onClose={() => setKeyDialog(false)}
        onCreated={(value) => {
          setKeyDialog(false);
          setSecret({ title: "New API key", value });
          void load();
        }}
        clientId={c.id}
      />
      <UsageDialog
        open={usageDialog}
        onClose={() => setUsageDialog(false)}
        clientId={c.id}
        onSaved={() => {
          setUsageDialog(false);
          void load();
        }}
      />
      <WebhookDialog
        open={webhookDialog}
        onClose={() => setWebhookDialog(false)}
        clientId={c.id}
        onCreated={(value) => {
          setWebhookDialog(false);
          setSecret({ title: "Webhook signing secret", value });
          void load();
        }}
      />
      <SecretDialog data={secret} onClose={() => setSecret(null)} />
    </>
  );
}

function Overview({
  client,
  onSaved,
}: {
  client: Client;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    ...client,
    monthlyImageLimit: String(client.monthlyImageLimit),
    priorityWeight: String(client.priorityWeight),
    retentionDays: String(client.retentionDays),
    maxFilesPerScan: client.maxFilesPerScan ?? "",
    maxFileSizeBytes: client.maxFileSizeBytes ?? "",
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });
  return (
    <CardContent>
      <Stack gap={2} maxWidth={760}>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <TextField
            label="Name"
            value={form.name}
            onChange={set("name")}
            fullWidth
          />
          <TextField
            label="Plan"
            value={form.plan}
            onChange={set("plan")}
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <TextField
            label="Monthly limit"
            type="number"
            value={form.monthlyImageLimit}
            onChange={set("monthlyImageLimit")}
            fullWidth
          />
          <TextField
            label="Priority weight"
            type="number"
            value={form.priorityWeight}
            onChange={set("priorityWeight")}
            fullWidth
          />
          <TextField
            label="Currency"
            value={form.defaultCurrency}
            onChange={set("defaultCurrency")}
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <TextField
            label="Retention days"
            type="number"
            value={form.retentionDays}
            onChange={set("retentionDays")}
            fullWidth
          />
          <TextField
            label="Max files override"
            type="number"
            value={form.maxFilesPerScan}
            onChange={set("maxFilesPerScan")}
            fullWidth
          />
          <TextField
            label="Max bytes override"
            type="number"
            value={form.maxFileSizeBytes}
            onChange={set("maxFileSizeBytes")}
            fullWidth
          />
        </Stack>
        <Box>
          <Button
            variant="contained"
            onClick={async () => {
              await api(`/admin/clients/${client.id}`, {
                method: "PATCH",
                body: jsonBody({
                  name: form.name,
                  plan: form.plan,
                  monthlyImageLimit: Number(form.monthlyImageLimit),
                  priorityWeight: Number(form.priorityWeight),
                  defaultCurrency: form.defaultCurrency,
                  retentionDays: Number(form.retentionDays),
                  maxFilesPerScan: form.maxFilesPerScan
                    ? Number(form.maxFilesPerScan)
                    : null,
                  maxFileSizeBytes: form.maxFileSizeBytes
                    ? Number(form.maxFileSizeBytes)
                    : null,
                }),
              });
              onSaved();
            }}
          >
            Save changes
          </Button>
        </Box>
      </Stack>
    </CardContent>
  );
}
function GenerateKeyDialog({
  open,
  onClose,
  onCreated,
  clientId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (v: string) => void;
  clientId: string;
}) {
  const [expiresAt, setExpires] = useState("");
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Generate API key</DialogTitle>
      <DialogContent>
        <TextField
          sx={{ mt: 1 }}
          type="datetime-local"
          label="Optional expiry"
          value={expiresAt}
          onChange={(e) => setExpires(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Alert severity="warning" sx={{ mt: 2 }}>
          The raw key is shown only once.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={async () => {
            const r = await api<{ apiKey: string }>(
              `/admin/clients/${clientId}/api-keys`,
              {
                method: "POST",
                body: jsonBody({
                  expiresAt: expiresAt
                    ? new Date(expiresAt).toISOString()
                    : null,
                }),
              },
            );
            onCreated(r.apiKey);
          }}
        >
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function UsageDialog({
  open,
  onClose,
  onSaved,
  clientId,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  clientId: string;
}) {
  const [units, setUnits] = useState("");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Adjust usage</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            label="Units (+ or -)"
            type="number"
            value={units}
            onChange={(e) => setUnits(e.target.value)}
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          <TextField
            label="Reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!units || reason.length < 3}
          onClick={async () => {
            await api(`/admin/clients/${clientId}/usage-adjustments`, {
              method: "POST",
              body: jsonBody({ units: Number(units), reason, reference }),
            });
            onSaved();
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function WebhookDialog({
  open,
  onClose,
  onCreated,
  clientId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (v: string) => void;
  clientId: string;
}) {
  const [url, setUrl] = useState("");
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Add completion webhook</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          sx={{ mt: 1 }}
          label="HTTPS URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!url}
          onClick={async () => {
            const r = await api<{ signingSecret: string }>(
              `/admin/clients/${clientId}/webhooks`,
              { method: "POST", body: jsonBody({ url }) },
            );
            onCreated(r.signingSecret);
          }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function SecretDialog({
  data,
  onClose,
}: {
  data: { title: string; value: string } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(data)} onClose={onClose} fullWidth>
      <DialogTitle>{data?.title}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Copy this value now. It cannot be retrieved later.
        </Alert>
        <Stack direction="row" gap={1}>
          <TextField
            fullWidth
            value={data?.value ?? ""}
            slotProps={{ input: { readOnly: true } }}
          />
          <IconButton
            onClick={() =>
              void navigator.clipboard.writeText(data?.value ?? "")
            }
          >
            <ContentCopyIcon />
          </IconButton>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          I saved it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
const date = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "—";
